/**
 * useMotivoResponsavel Hook - Manages Motivo/Responsável CRUD operations
 */
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { toast } from 'react-hot-toast';
import { useConfirm } from './useConfirm';

interface Motivo {
  id: number;
  motivo: string;
  responsavel: string;
}

interface NewMotivo {
  motivo: string;
  responsavel: string;
}

interface UseMotivoResponsavelReturn {
  motivos: Motivo[];
  isLoadingMotivos: boolean;
  isSavingMotivo: boolean;
  editingMotivo: number | null;
  newMotivo: NewMotivo;
  editMotivoData: NewMotivo;
  setNewMotivo: (motivo: NewMotivo) => void;
  setEditMotivoData: (motivo: NewMotivo) => void;
  handleCreateMotivo: () => Promise<void>;
  handleUpdateMotivo: (id: number) => Promise<void>;
  handleDeleteMotivo: (id: number) => Promise<void>;
  startEditing: (motivo: Motivo) => void;
  cancelEditing: () => void;
  fetchMotivos: () => Promise<void>;
  ConfirmDialog: ReturnType<typeof useConfirm>['ConfirmDialog'];
}

export function useMotivoResponsavel(activeSection: string): UseMotivoResponsavelReturn {
  const { confirm, ConfirmDialog } = useConfirm();

  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const [isLoadingMotivos, setIsLoadingMotivos] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState<number | null>(null);
  const [newMotivo, setNewMotivo] = useState<NewMotivo>({ motivo: '', responsavel: '' });
  const [editMotivoData, setEditMotivoData] = useState<NewMotivo>({ motivo: '', responsavel: '' });
  const [isSavingMotivo, setIsSavingMotivo] = useState(false);

  const fetchMotivos = useCallback(async () => {
    setIsLoadingMotivos(true);
    try {
      const response = await apiClient.get('/config/motivo');
      setMotivos(response.data);
    } catch (error) {
      console.error('Error fetching motivos:', error);
      toast.error('Erro ao carregar lista de Motivos/Responsáveis');
    } finally {
      setIsLoadingMotivos(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'motivo') {
      fetchMotivos();
    }
  }, [activeSection, fetchMotivos]);

  const handleCreateMotivo = useCallback(async () => {
    if (!newMotivo.motivo.trim() || !newMotivo.responsavel.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setIsSavingMotivo(true);
    try {
      const response = await apiClient.post('/config/motivo', newMotivo);
      setMotivos(prev => [...prev, response.data]);
      setNewMotivo({ motivo: '', responsavel: '' });
      toast.success('Adicionado com sucesso');
    } catch (error: any) {
      console.error('Error creating motivo:', error);
      toast.error(error.response?.data?.message || 'Erro ao adicionar');
    } finally {
      setIsSavingMotivo(false);
    }
  }, [newMotivo]);

  const handleUpdateMotivo = useCallback(async (id: number) => {
    if (!editMotivoData.motivo.trim() || !editMotivoData.responsavel.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setIsSavingMotivo(true);
    try {
      const response = await apiClient.put(`/config/motivo/${id}`, editMotivoData);
      setMotivos(prev => prev.map(m => m.id === id ? response.data : m));
      setEditingMotivo(null);
      toast.success('Atualizado com sucesso');
    } catch (error: any) {
      console.error('Error updating motivo:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar');
    } finally {
      setIsSavingMotivo(false);
    }
  }, [editMotivoData]);

  const handleDeleteMotivo = useCallback(async (id: number) => {
    const confirmed = await confirm({
      title: 'Excluir Motivo/Responsável',
      message: 'Tem certeza que deseja excluir este registro?',
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      await apiClient.delete(`/config/motivo/${id}`);
      setMotivos(prev => prev.filter(m => m.id !== id));
      toast.success('Excluído com sucesso');
    } catch (error: any) {
      console.error('Error deleting motivo:', error);
      toast.error('Erro ao excluir');
    }
  }, [confirm]);

  const startEditing = useCallback((motivo: Motivo) => {
    setEditingMotivo(motivo.id);
    setEditMotivoData({ motivo: motivo.motivo, responsavel: motivo.responsavel });
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingMotivo(null);
    setEditMotivoData({ motivo: '', responsavel: '' });
  }, []);

  return {
    motivos,
    isLoadingMotivos,
    isSavingMotivo,
    editingMotivo,
    newMotivo,
    editMotivoData,
    setNewMotivo,
    setEditMotivoData,
    handleCreateMotivo,
    handleUpdateMotivo,
    handleDeleteMotivo,
    startEditing,
    cancelEditing,
    fetchMotivos,
    ConfirmDialog,
  };
}
